import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Layers, Loader2, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr } from '../../../components/admin/billing/shared';

const HEAD_COLOURS = {
  'Detailing': '#14b8a6',
  'Mechanical Servicing': '#0ea5e9',
  'Body Shop': '#f97316',
  'Accessories': '#8b5cf6',
  'Consumables and Others': '#94a3b8',
};

const currentYM = () => {
  const d = new Date();
  return { y: d.getFullYear(), m: d.getMonth() + 1 };
};

const shiftMonth = ({ y, m }, delta) => {
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1 };
};

const monthShort = (ym) => {
  if (!ym) return '';
  const [yy, mm] = ym.split('-').map((s) => Number(s));
  const d = new Date(Date.UTC(yy, mm - 1, 1));
  return d.toLocaleString('en-IN', { month: 'short', timeZone: 'UTC' });
};

const RevenueHeadsReport = () => {
  const { authHeader } = useAuth();
  const [{ y, m }, setYm] = useState(currentYM());
  const [source, setSource] = useState('invoices');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const { data } = await axios.get(`${API}/gst/reports/revenue-heads`, {
        headers: authHeader(),
        params: { year: y, month: m, source },
      });
      setData(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [authHeader, y, m, source]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const heads = data?.heads || [];
  const totals = data?.totals || { taxable: 0, gst: 0, grand_total: 0 };
  const monthLabel = data?.month_label || '';

  const csv = useMemo(() => {
    if (!heads.length) return '';
    const rows = [['Head', 'Invoices', 'Line items', 'Taxable (₹)', 'GST (₹)', 'Grand total (₹)', '% of month']];
    heads.forEach((h) => rows.push([h.head, h.invoice_count, h.line_count, h.taxable, h.gst, h.grand_total, h.pct_of_month]));
    rows.push(['TOTAL', '', '', totals.taxable, totals.gst, totals.grand_total, 100]);
    return rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  }, [heads, totals]);

  const downloadCsv = () => {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `revenue_heads_${y}_${String(m).padStart(2, '0')}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-6 bg-slate-50 min-h-full space-y-4" data-testid="revenue-heads-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Layers className="text-indigo-600" size={22} />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Revenue Heads</h1>
          <span className="ml-2 text-sm text-slate-500">
            {loading ? '—' : `${data?.invoice_count || 0} ${source === 'estimates' ? 'estimates' : 'invoices'} · ₹ ${fmtINR(totals.grand_total)}`}
          </span>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/reports" className="text-xs bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-1.5 rounded-lg" data-testid="back-to-reports">
            ← Reports hub
          </Link>
          <button
            type="button"
            onClick={downloadCsv}
            disabled={!heads.length}
            className="text-xs bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5"
            data-testid="download-csv"
          >
            <Download size={13} /> CSV
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="month-selector">
        <button type="button" onClick={() => setYm(shiftMonth({ y, m }, -1))} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Previous month" data-testid="prev-month">
          <ChevronLeft size={16} />
        </button>
        <input
          type="month"
          value={`${y}-${String(m).padStart(2, '0')}`}
          onChange={(e) => {
            const [yy, mm] = e.target.value.split('-').map((s) => Number(s));
            if (yy && mm) setYm({ y: yy, m: mm });
          }}
          className="text-sm bg-slate-50 border border-slate-200 rounded-md px-2 py-1"
          data-testid="month-input"
        />
        <button type="button" onClick={() => setYm(shiftMonth({ y, m }, 1))} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Next month" data-testid="next-month">
          <ChevronRight size={16} />
        </button>
        <button
          type="button"
          onClick={() => setYm(currentYM())}
          className="text-xs bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-2.5 py-1 rounded-md"
          data-testid="this-month-btn"
        >
          This month
        </button>
        <span className="ml-2 text-xs text-slate-500">Showing <b>{monthLabel}</b></span>
        <div className="ml-auto inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="source-toggle">
          <button
            type="button"
            onClick={() => setSource('invoices')}
            className={`text-xs px-3 py-1.5 ${source === 'invoices' ? 'bg-indigo-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            data-testid="source-invoices"
          >Invoices</button>
          <button
            type="button"
            onClick={() => setSource('estimates')}
            className={`text-xs px-3 py-1.5 ${source === 'estimates' ? 'bg-indigo-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            data-testid="source-estimates"
          >Estimates (pipeline)</button>
        </div>
      </div>

      {/* 12-month stacked bar chart */}
      {!loading && data?.trend && data.trend.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="trend-chart">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-slate-900">12-month trend by head</h2>
            <span className="text-[10px] text-slate-400">Grand total per month, stacked by revenue head</span>
          </div>
          {(() => {
            const months = data.series_months || [];
            // Compute monthly totals + max for scaling.
            const monthTotals = months.map((mn) => {
              return data.trend.reduce((sum, t) => {
                const cell = (t.series || []).find((s) => s.month === mn);
                return sum + (cell ? cell.total : 0);
              }, 0);
            });
            const maxTotal = Math.max(1, ...monthTotals);
            return (
              <div>
                <div className="flex items-end gap-1.5 h-40" data-testid="trend-bars">
                  {months.map((mn, mi) => {
                    const total = monthTotals[mi];
                    const barPct = Math.round((total / maxTotal) * 100);
                    return (
                      <div key={mn} className="flex-1 flex flex-col items-center justify-end min-w-[24px]" title={`${monthShort(mn)}: ₹ ${fmtINR(total)}`}>
                        <div className="w-full flex flex-col-reverse rounded-sm overflow-hidden" style={{ height: `${Math.max(2, barPct)}%` }}>
                          {data.trend.map((t) => {
                            const cell = (t.series || []).find((s) => s.month === mn);
                            const v = cell ? cell.total : 0;
                            if (v <= 0) return null;
                            const segPct = total > 0 ? (v / total) * 100 : 0;
                            return (
                              <div
                                key={t.head}
                                style={{ height: `${segPct}%`, backgroundColor: HEAD_COLOURS[t.head] || '#64748b' }}
                                title={`${t.head} · ${monthShort(mn)}: ₹ ${fmtINR(v)}`}
                              />
                            );
                          })}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1 whitespace-nowrap">
                          {monthShort(mn)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Legend */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  {data.trend.map((t) => (
                    <span key={t.head} className="inline-flex items-center gap-1.5 text-[11px] text-slate-600">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: HEAD_COLOURS[t.head] || '#64748b' }} />
                      {t.head}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="rh-error">{err}</div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">
            <Loader2 className="inline animate-spin mr-2" size={16} /> Loading…
          </div>
        ) : heads.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="rh-empty">
            No {source === 'estimates' ? 'estimates' : 'GST invoices'} in {monthLabel}. Try a different month.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold text-slate-600">Head</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Invoices</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Lines</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right whitespace-nowrap">Taxable</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right whitespace-nowrap">GST</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right whitespace-nowrap">Grand total</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 min-w-[160px]">Share of month</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {heads.map((h) => {
                  const colour = HEAD_COLOURS[h.head] || '#64748b';
                  const slug = h.head.replace(/\s+/g, '-').toLowerCase();
                  return (
                    <tr key={h.head} className="hover:bg-slate-50" data-testid={`rh-row-${slug}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colour }} />
                          <span className="text-slate-900 font-medium">{h.head}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">{h.invoice_count}</td>
                      <td className="px-3 py-2 text-right text-slate-500">{h.line_count}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-700 whitespace-nowrap">₹ {fmtINR(h.taxable)}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500 whitespace-nowrap">₹ {fmtINR(h.gst)}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-900 font-semibold whitespace-nowrap">₹ {fmtINR(h.grand_total)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${h.pct_of_month}%`, backgroundColor: colour }}
                              data-testid={`rh-bar-${slug}`}
                            />
                          </div>
                          <span className="text-[11px] text-slate-500 w-9 text-right">{h.pct_of_month}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200 font-semibold">
                <tr>
                  <td className="px-3 py-2 text-slate-600" colSpan={3}>Total</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-900 whitespace-nowrap">₹ {fmtINR(totals.taxable)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-900 whitespace-nowrap">₹ {fmtINR(totals.gst)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-900 whitespace-nowrap" data-testid="rh-grand-total">₹ {fmtINR(totals.grand_total)}</td>
                  <td className="px-3 py-2 text-right text-slate-500">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <p className="text-[11px] text-slate-400 px-1">
        {source === 'estimates'
          ? 'Aggregated from GST estimate line items with status draft, sent, or accepted — treat this as a pipeline view of upcoming revenue by head.'
          : 'Aggregated from booked GST invoice line items (every status except draft and cancelled — includes partially paid).'}
        {' '}Any line without an explicit revenue head is auto-classified from the description
        (Ceramic / PPF / Repaint keywords etc.). Change the tag on any line to move it between heads instantly.
      </p>
    </div>
  );
};

export default RevenueHeadsReport;
